import type { GroupNode, LayerConfig, TreeNode } from '../api/types';

/** Visibility state keyed by layer id, or `group:<id>` for groups. */
export type VisibilityMap = Readonly<Record<string, boolean>>;

export const isGroup = (node: TreeNode): node is GroupNode => 'group' in node;

export const groupKey = (groupId: string): string => `group:${groupId}`;

export const nodeKey = (node: TreeNode): string => (isGroup(node) ? groupKey(node.group) : node.layer);

/** Drop layer references the config does not define, then groups left empty. Order is kept. */
export function pruneTree(tree: readonly TreeNode[], known: ReadonlySet<string>): TreeNode[] {
  const out: TreeNode[] = [];
  for (const node of tree) {
    if (isGroup(node)) {
      const children = pruneTree(node.children, known);
      if (children.length > 0) out.push({ ...node, children });
    } else if (known.has(node.layer)) {
      out.push(node);
    }
  }
  return out;
}

/** Layer ids in tree order (top of the panel first). */
export function flattenLayerIds(tree: readonly TreeNode[]): string[] {
  return tree.flatMap((n) => (isGroup(n) ? flattenLayerIds(n.children) : [n.layer]));
}

/** The config's own visibility for every node in the tree: the baseline user changes are measured against. */
export function defaultVisibility(
  tree: readonly TreeNode[],
  layers: ReadonlyMap<string, Pick<LayerConfig, 'visible'>>,
): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  const walk = (nodes: readonly TreeNode[]): void => {
    for (const n of nodes) {
      if (isGroup(n)) {
        out[groupKey(n.group)] = n.visible ?? true;
        walk(n.children);
      } else {
        out[n.layer] = layers.get(n.layer)?.visible ?? false;
      }
    }
  };
  walk(tree);
  return out;
}

/** A layer is drawn only when it and every enclosing group are switched on. */
export function effectiveVisibleLayers(tree: readonly TreeNode[], vis: VisibilityMap): Set<string> {
  const out = new Set<string>();
  const walk = (nodes: readonly TreeNode[], parentOn: boolean): void => {
    for (const n of nodes) {
      if (isGroup(n)) walk(n.children, parentOn && (vis[groupKey(n.group)] ?? true));
      else if (parentOn && (vis[n.layer] ?? false)) out.add(n.layer);
    }
  };
  walk(tree, true);
  return out;
}

/** Inclusive min, exclusive max (MapLibre semantics). */
export function inZoomRange(zoom: number, range: { min: number; max?: number }): boolean {
  return zoom >= range.min && (range.max === undefined || zoom < range.max);
}
